import { Button } from './ui/button';
import { Download, FileText, Image as ImageIcon } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { Modal } from './Modal';
import { formatFileName } from '@/lib/utils';
import type { DocumentViewerProps } from '@/types';

const DEMO_PDF_URL =
  'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';

export function resolveDocumentPreviewUrl(documentUrl?: string): string | undefined {
  if (!documentUrl) return undefined;

  if (documentUrl.includes('sample-pdf-data')) {
    return DEMO_PDF_URL;
  }

  if (documentUrl.includes('via.placeholder.com')) {
    const textMatch = documentUrl.match(/text=([^&]+)/);
    const text = textMatch
      ? decodeURIComponent(textMatch[1].replace(/\+/g, ' '))
      : 'Document';
    return `https://placehold.co/600x400/png?text=${encodeURIComponent(text)}`;
  }

  return documentUrl;
}

const getFileExtension = (filename: string) =>
  filename.split('.').pop()?.toLowerCase() || '';

const isImage = (filename: string, url?: string) => {
  const ext = getFileExtension(filename);
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return true;
  if (url?.startsWith('data:image/')) return true;
  if (url?.startsWith('blob:') && !isPDF(filename, url)) return true;
  return false;
};

const isPDF = (filename: string, url?: string) => {
  if (getFileExtension(filename) === 'pdf') return true;
  if (url?.includes('application/pdf')) return true;
  return false;
};

export function DocumentViewer({
  isOpen,
  onClose,
  documentName,
  documentUrl,
  title,
}: DocumentViewerProps) {
  const previewUrl = resolveDocumentPreviewUrl(documentUrl);

  const handleDownload = () => {
    if (!previewUrl) return;

    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = documentName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const renderDocumentPreview = () => {
    if (!previewUrl) {
      return (
        <div className="flex h-96 flex-col items-center justify-center rounded-lg bg-muted">
          <FileText className="mb-4 h-16 w-16 text-muted-foreground" />
          <p className="text-lg font-medium">{documentName}</p>
          <p className="mt-2 text-sm text-muted-foreground">Document preview is not available</p>
        </div>
      );
    }

    if (isImage(documentName, previewUrl)) {
      return (
        <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2">
          <ImageWithFallback
            src={previewUrl}
            alt={documentName}
            className="mx-auto h-auto max-h-[65vh] w-full rounded-md object-contain"
          />
        </div>
      );
    }

    if (isPDF(documentName, previewUrl)) {
      return (
        <iframe
          src={previewUrl}
          title={documentName}
          className="h-[70vh] w-full rounded-lg border border-gray-200 bg-white"
        />
      );
    }

    return (
      <div className="flex h-96 flex-col items-center justify-center rounded-lg bg-muted">
        <FileText className="mb-4 h-16 w-16 text-muted-foreground" />
        <p className="font-medium">{documentName}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          File type: {getFileExtension(documentName).toUpperCase() || 'Unknown'}
        </p>
        <Button onClick={handleDownload} className="mt-4">
          <Download className="mr-2 h-4 w-4" />
          Download File
        </Button>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="max-w-3xl"
      headerVariant="drawer"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            {isImage(documentName, previewUrl) ? (
              <ImageIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
            ) : (
              <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
            )}
            <span className="text-sm font-medium text-gray-800" title={documentName}>
              {formatFileName(documentName)}
            </span>
          </div>

          {previewUrl && (
            <Button variant="outline" size="sm" className="h-8 shrink-0 border-gray-200 bg-white" onClick={handleDownload}>
              <Download className="mr-2 h-3.5 w-3.5" />
              Download
            </Button>
          )}
        </div>

        {renderDocumentPreview()}
      </div>
    </Modal>
  );
}
